/** Home feed (t131u): one curated, time-ordered stream of what's happening, built from existing sources under their own visibility rules. */
import type { KudosPost } from '../types';
import type { Announcement } from '../kb/types';
import type { AckListItem } from '../acks';
import type { CalendarEventView } from '../calendar';
import type { ShiftOffer } from '../workforce/types';
import type { FeedIssueChange } from './issueActivity';

export const FEED_TYPES = ['announcements', 'kudos', 'birthdays', 'events', 'discussions', 'acks', 'offers', 'tasks', 'issues'] as const;
export type FeedType = (typeof FEED_TYPES)[number];
export const FEED_TYPE_LABEL: Record<FeedType, string> = {
  announcements: 'Announcements', kudos: 'Kudos', birthdays: 'Birthdays', events: 'Events', discussions: 'Discussions', acks: 'Acknowledgements', offers: 'Shift offers', tasks: 'Tasks done', issues: 'Issues',
};
/** all: everything you can see. buildings / teams: items tied to your buildings or your departments and groups. */
export type FeedScope = 'all' | 'buildings' | 'teams';
export const FEED_SCOPE_LABEL: Record<FeedScope, string> = { all: 'Everywhere', buildings: 'My buildings', teams: 'My teams' };

export interface FeedTask {
  id: string; title: string; key: string | null; project_id: string; project_name: string | null; no_project: boolean;
  /** t701u: the task's building. */ location_id?: string | null;
  location_name: string | null; /** t701u: the building's short code (the card's top right). */ location_code?: string | null;
  actor_id: string | null; actor_name: string | null; href: string;
  /** t298u: every comment on the task (the card shows the latest one or two and "View all N comments"). */ comment_count?: number;
  /** t362u: the task's status category: 'done' (green Completed strip) or 'cancelled' (grey). Absent from older servers: done. */
  status_category?: string | null;
}
export interface FeedIssue {
  id: string; ref: number; title: string; /** t212u: plain-text excerpt */ description?: string | null; severity: string; status: string; type: string; location_name: string; /** The building, so its name opens its page. */ location_id?: string | null; /** t701u: its short code. */ location_code?: string | null; reporter_id: string | null; reporter_name: string; assignee_id: string | null; assignee_name: string | null; /** t699u: everyone it is assigned to. */ assignees?: { id: string; name: string }[];
  /** t298u: the post's meta line and thumbnail: the area within the building, when it was reported, its first photo. */
  location_detail?: string | null; created_at?: string; photo_id?: string | null; comment_count?: number;
  /** t362u: when it was resolved / closed (the card's strip says "Resolved 20 Oct", "Closed 20 Oct"). */
  resolved_at?: string | null; closed_at?: string | null;
}
/** One comment as a feed card shows it. */
export interface FeedComment {
  actor_id: string | null; actor_name: string; body: string; at: string;
  /** t358u: a reply in someone's thread: whose comment it answers (the preview and Latest line say "replied"). */
  reply_to?: string | null;
  /** t358u: written by the viewer (shown as "You"). */
  mine?: boolean;
}
/** A discussion card (t156u): new comments on one thing you follow or are involved in, grouped within a window. */
export interface FeedThread {
  entity_type: 'calendar_event' | 'incident' | 'task' | 'kudos' | (string & {});
  entity_id: string;
  /** e.g. "Store window refresh", "#3 Card terminal offline", "WH-12 Restock aisle 4". */
  title: string;
  link: string;
  /** Comments in this card (never your own). */
  count: number;
  actors: { id: string; name: string }[];
  /** Newest first, up to 3. */
  comments: FeedComment[];
  /**
   * t358u: whether the card has comments for you that you haven't read since your own latest comment (the speech
   * bubble's dot). Absent from older servers: then any comment in the card counts.
   */
  unread?: boolean;
  /** t358u: the newest of these comments you haven't read (the server works out `unread` from it). */
  unread_at?: string | null;
  /** How a quick reply is posted: into the entity's thread conversation, as a task comment, or as a kudos comment. */
  reply: { kind: 'thread'; conversation_id: string } | { kind: 'task' } | { kind: 'kudos' } | null;
  /**
   * t174u: what happened to the item itself, shown inline with the comments in time order as quiet lines
   * ("Sam Patel completed this task · 11:31"). Items with an entity thread (issues, events) read theirs from the thread.
   */
  events?: { actor_id: string | null; actor_name: string; text: string; at: string }[];
  /** t362u: the discussed task or issue is done or closed (the card gets the same strip as its own card would). */
  closure?: FeedClosure | null;
}
/**
 * t362u: a done or closed task / issue. `done` (green strip): fixed or finished: issue Resolved, task in a Done
 * status. `closed` (grey strip): closed without a fix: issue Closed, task in a Cancelled status ("Won't do").
 */
export interface FeedClosure { tone: 'done' | 'closed'; label: string; at: string | null }
/** t174u: the latest thing that happened to an issue itself (comments are on the card's thread). */
export type FeedIssueEvent = 'reported' | 'resolved' | 'updated';
/** t174u: activity merged onto an item's one card: new comments, and when the item's own latest event happened. */
export interface FeedActivity {
  thread?: FeedThread; event_at?: string;
  /**
   * t188u: the newest comment on the item's thread when you weren't sent it (announcements, issues and events you
   * don't follow), so every card can show its latest comment. Not counted as new.
   * t358u: always the newest comment of any kind, replies and the viewer's own included (comment notifications never
   * include your own, so the thread alone said "Latest: Euan commented" after you had answered him).
   */
  latest_comment?: FeedComment;
}
export type FeedItem =
  | ({ kind: 'announcement'; id: string; at: string; announcement: Announcement } & FeedActivity)
  | ({ kind: 'kudos' | 'birthday'; id: string; at: string; kudos: KudosPost } & FeedActivity)
  | { kind: 'ack'; id: string; at: string; ack: AckListItem }
  /** t145: a shift offered to you that is waiting for your answer (personal, any scope). */
  | { kind: 'shift_offer'; id: string; at: string; offer: ShiftOffer }
  | ({ kind: 'task_completed'; id: string; at: string; task: FeedTask } & FeedActivity)
  /**
   * One card per issue (t174u): `event` / `actor` are its own latest event (who reported, resolved or last changed it).
   * t246u: `changes` is what that latest change was (fields saved together), so the card can say what changed.
   */
  | ({ kind: 'issue'; id: string; at: string; event: FeedIssueEvent; actor: { id: string; name: string } | null; changes?: FeedIssueChange[]; issue: FeedIssue } & FeedActivity)
  /** created: a company / building / department event you can see; invited: you're on the guest list (personal events only this way). */
  | ({ kind: 'event'; id: string; at: string; reason: 'created' | 'invited'; event: CalendarEventView } & FeedActivity)
  | { kind: 'discussion'; id: string; at: string; thread: FeedThread };

/**
 * Feed vs notifications (t156u). Rule: anything that notifies you about discussion or content also gets a feed
 * card; purely operational or personal alerts (a missed checklist, a closing store, your shift reminder) stay
 * notification-only. Every notification kind the API sends must be listed here (a test checks the API source).
 * The value is the feed card that covers it, or null for notification-only.
 */
export const NOTIFICATION_FEED_CARD: Record<string, FeedItem['kind'] | null> = {
  // Discussion and content → feed
  'calendar.thread_message': 'discussion', 'ops.incident_comment': 'discussion', 'task.commented': 'discussion', 'task.mention': 'discussion',
  'people.kudos_comment': 'discussion', 'announcement.comment': 'discussion', 'workforce.shift_offer_comment': 'discussion', 'ops.equipment_comment': 'discussion', 'fleet.journey_comment': 'discussion', 'message.mention': 'discussion', // mentions in an issue / event discussion; chat mentions stay in Chat
  'calendar.invite': 'event', 'people.kudos': 'kudos', 'people.birthday': 'birthday', 'announcement.reminder': 'announcement',
  'ack.requested': 'ack', 'ack.changed': 'ack', 'workforce.shift_offer': 'shift_offer', 'task.completed': 'task_completed', 'ops.incident_new': 'issue', 'ops.incident_status': 'issue',
  // Operational or personal → notification only
  'message.thread_reply': null, 'message.scheduled_failed': null, 'call.missed': null, 'call.incoming': null, 'calendar.rsvp': null, 'calendar.reminder': null, 'building.task': null, 'building.chat': null,
  'ack.reminder': null, 'ack.acknowledged': null, 'ack.overdue_summary': null, 'ack.overdue': null, 'ack.due_soon': null, 'task.assigned': null, 'task.due': null, 'todo.reminder': null,
  'poll.reminder': null, 'poll.closed': null, 'ops.incident_sla': null, 'ops.incident_due': null, 'ops.incident_riddor': null, 'ops.incident_assigned': null,
  'ops.checklist_missed': null, 'ops.equipment_due': null, 'ops.checklist_assigned': null, 'onboarding.step_due': null, 'onboarding.completed': null, 'onboarding.buddy': null,
  'onboarding.assigned': null, 'training.assigned': null, 'training.due': null, 'kb.review': null, 'kb.published': null, 'kb.policy_ack': null, 'kb.changes_requested': null, 'form.status': null,
  'fleet.compliance': null, 'fleet.journey.assigned': null, 'fleet.journey.rescheduled': null, 'fleet.journey.stopped': null, 'ai.digest': null,
  'workforce.visitor_arrived': null, 'workforce.roll_call': null, 'workforce.shift_offer_response': null, 'workforce.shift_offer_decided': null, 'workforce.skill_expiring': null,
  'workforce.shift_confirm': null, 'workforce.shift_cancelled': null, 'workforce.wtr.withdrawn': null, 'workforce.wtr.signed': null,
  'workforce.wtr.requested': null, 'workforce.timesheet_rejected': null, 'workforce.timesheet_queried': null, 'workforce.swap_update': null,
  'workforce.swap_decided': null, 'workforce.shift_dropped': null, 'workforce.rota_change': null, 'workforce.swap_approval': null, 'workforce.still_clocked_in': null, 'workforce.shift_reminder': null,
  'workforce.schedule_published': null, 'workforce.open_shifts': null, 'workforce.open_shift_claim_decided': null, 'workforce.open_shift_claim': null,
  'workforce.leave_request': null, 'workforce.leave_decided': null, 'workforce.handover_reminder': null, 'workforce.handover': null,
  'workforce.auto_clocked_out': null, 'workforce.missed_clock_in': null, 'workforce.missed_clock_in_manager': null,
  'feedback.status': null, 'feedback.new': null, // t171u: bug reports and suggestions
  'hr.bank_changed': null, 'hr.change_requested': null, 'hr.change_approved': null, 'hr.change_rejected': null, 'hr.details_missing': null, // t368u/t413u: HR records (personal, never on the feed)
  'security.mfa_required': null, 'security.mfa_reset': null, // t462u: someone's own two-factor (personal, never on the feed)
  'policy.review_requested': null, 'policy.approved': null, 'policy.changes_requested': null, 'policy.comment': null, 'policy.ack': null, 'policy.published': null, // t563u: policies
  'policy.ack_reminder': null, 'policy.review_due': null, 'policy.review_overdue': null,
  'account.deletion_request': null, 'account.deletion_declined': null, 'account.deletion_reminder': null, 'moderation.report': null, // owner/admin only, never on the feed
};
/** Notification kinds that become discussion cards. */
export const DISCUSSION_KINDS = Object.entries(NOTIFICATION_FEED_CARD).filter(([, v]) => v === 'discussion').map(([k]) => k);

export interface FeedPage {
  items: FeedItem[];
  /** Pass as `before` for the next page; null at the end. */
  next_before: string | null;
  /** First page only: pinned announcements, shown above the stream (and left out of it). */
  pinned?: Announcement[];
  /** First page only (t164u): unread comments on each pinned post, so its card shows them instead of a discussion card below. */
  pinned_activity?: Record<string, PinnedActivity>;
  /** First page only (t188u): each pinned post's newest comment, for its card's preview line. */
  pinned_comments?: Record<string, FeedThread['comments'][number]>;
}
export interface PinnedActivity { count: number; actors: { id: string; name: string }[]; latest_at: string }

/**
 * t164u: what a feed item is about, as `entity_type:id` refs (announcement, kudos, calendar_event, incident, task, message).
 * A pinned item must not show again further down: its own card, discussion cards about it, or anything else about it.
 */
export function feedItemRefs(i: FeedItem): string[] {
  switch (i.kind) {
    case 'announcement': return [`announcement:${i.announcement.id}`];
    case 'kudos': case 'birthday': return [`kudos:${i.kudos.id}`];
    case 'ack': return [`message:${i.ack.message_id}`];
    case 'shift_offer': return [`shift_offer:${i.offer.id}`];
    case 'task_completed': return [`task:${i.task.id}`];
    case 'issue': return [`incident:${i.issue.id}`];
    case 'event': return [`calendar_event:${i.event.id}`];
    case 'discussion': return [`${i.thread.entity_type}:${i.thread.entity_id}`];
  }
}
export const pinnedRefs = (pinned: Pick<Announcement, 'id'>[] | undefined) => new Set((pinned ?? []).map(a => `announcement:${a.id}`));
export const isAboutPinned = (i: FeedItem, refs: Set<string>) => refs.size > 0 && feedItemRefs(i).some(r => refs.has(r));

/**
 * The stream across loaded pages: never an item about a pinned post, and never the same card twice (pages are cut by
 * time, so a regrouped card can come back on a later page).
 */
export function feedStream(pages: Pick<FeedPage, 'items'>[], pinned: Pick<Announcement, 'id'>[] | undefined): FeedItem[] {
  const refs = pinnedRefs(pinned);
  const seen = new Set<string>();
  const out: FeedItem[] = [];
  for (const p of pages) for (const i of p.items) {
    // t174u: one card per entity too; pages are newest first, so the first (freshest) card for it wins.
    const keys = [i.id, ...feedItemRefs(i)];
    if (keys.some(k => seen.has(k)) || isAboutPinned(i, refs)) continue;
    for (const k of keys) seen.add(k);
    out.push(i);
  }
  return out;
}

/** Entity types a discussion can be merged into, by the filter that shows that entity's own card. */
export const FEED_TYPE_ENTITY: Partial<Record<FeedType, string>> = { announcements: 'announcement', kudos: 'kudos', birthdays: 'kudos', events: 'calendar_event', tasks: 'task', issues: 'incident', offers: 'shift_offer' };

const newest = <T extends { at: string }>(xs: T[]) => xs.reduce((a, b) => (b.at > a.at ? b : a));
function mergeThreads(ts: FeedThread[]): FeedThread {
  const sorted = [...ts].sort((a, b) => ((b.comments[0]?.at ?? '') > (a.comments[0]?.at ?? '') ? 1 : -1));
  const head = sorted[0];
  const actors = new Map<string, { id: string; name: string }>();
  for (const t of sorted) for (const a of t.actors) if (!actors.has(a.id)) actors.set(a.id, a);
  return {
    ...head, count: sorted.reduce((n, t) => n + t.count, 0), actors: [...actors.values()],
    comments: sorted.flatMap(t => t.comments).sort((a, b) => (a.at < b.at ? 1 : -1)).slice(0, 3),
    unread_at: sorted.map(t => t.unread_at ?? '').reduce((a, b) => (b > a ? b : a), '') || null,
    ...(sorted.some(t => t.unread !== undefined) ? { unread: sorted.some(t => t.unread ?? t.count > 0) } : {}),
    reply: sorted.find(t => t.reply)?.reply ?? null,
  };
}

/**
 * t174u: one card per entity. Everything about one issue, task, event, post or kudos (its own event cards and
 * discussion cards) becomes a single card dated by the newest activity, so new activity moves it to the top instead
 * of adding another. The item's own card is kept, with the comments attached as `thread`; with no own card in the
 * batch, a lone discussion card stays (only when `keepDiscussions`, i.e. the Discussions filter is on).
 */
export function mergeFeedByEntity(items: FeedItem[], keepDiscussions = true): FeedItem[] {
  const groups = new Map<string, FeedItem[]>();
  for (const i of items) {
    const k = feedItemRefs(i)[0];
    const g = groups.get(k);
    if (g) g.push(i); else groups.set(k, [i]);
  }
  const out: FeedItem[] = [];
  for (const g of groups.values()) {
    const talks = g.filter((i): i is Extract<FeedItem, { kind: 'discussion' }> => i.kind === 'discussion');
    const own = g.filter(i => i.kind !== 'discussion');
    const thread = talks.length ? mergeThreads(talks.map(t => t.thread)) : undefined;
    const at = newest(g).at;
    if (!own.length) {
      if (keepDiscussions && thread) out.push({ kind: 'discussion', id: `d:${thread.entity_type}:${thread.entity_id}`, at, thread });
      continue;
    }
    const base = newest(own);
    if (g.length === 1) { out.push(base); continue; }
    const merged = { ...base, at, event_at: base.at, ...(thread ? { thread } : {}) } as FeedItem;
    if (merged.kind === 'task_completed' && thread) {
      merged.thread = { ...thread, events: [{ actor_id: merged.task.actor_id, actor_name: merged.task.actor_name ?? 'Someone', text: 'completed this task', at: base.at }] };
    }
    out.push(merged);
  }
  return out;
}
export interface FeedQuery { types?: FeedType[]; scope?: FeedScope; before?: string; limit?: number }

/** t162: notification kinds that belong to a feed item; interacting with the item marks these read (notification-only kinds are left alone). */
export const FEED_NOTIFICATION_KINDS = Object.entries(NOTIFICATION_FEED_CARD).filter(([, v]) => v !== null).map(([k]) => k);

/**
 * t277u: where the "Earlier · already seen" divider goes: above the first item at or before the previous visit,
 * and only when something newer sits above it. -1 means no divider (first visit, nothing new, or nothing older loaded).
 */
export function feedNewSinceIndex(items: readonly Pick<FeedItem, 'at'>[], since: string | null | undefined): number {
  if (!since) return -1;
  const t = Date.parse(since);
  if (Number.isNaN(t)) return -1;
  const i = items.findIndex(x => Date.parse(x.at) <= t);
  return i > 0 ? i : -1;
}

/** t358u: the newest comment a card knows of: its latest comment (any kind, yours too) or the newest one sent to you. */
export function feedNewestComment(i: FeedItem): FeedComment | undefined {
  const th = 'thread' in i ? i.thread?.comments[0] : undefined;
  const lc = 'latest_comment' in i ? i.latest_comment : undefined;
  return th && lc ? (lc.at >= th.at ? lc : th) : th ?? lc;
}
/** t358u: the speech bubble's dot: comments for you that you haven't read since your own latest comment. */
export const feedThreadUnread = (th: FeedThread | undefined) => Boolean(th && (th.unread ?? th.count > 0));
